import React, { useState } from 'react'
import axios from 'axios'
import Grid from '@material-ui/core/Grid'
import TextField from '@material-ui/core/TextField'
import Button from '@material-ui/core/Button'
import Typography from '@material-ui/core/Typography'
import CircularProgress from '@material-ui/core/CircularProgress'
import { useTranslation } from 'react-i18next'

import { apiBase } from './api'
import { validateCustomer } from './cart'

/**
 * Who is paying — the step that turns a link into an attempt.
 *
 * It owns the checkout POST and hands `{hash_id, iframe_url}` up to Checkout. It has to own
 * it: the gateway session is minted against the details typed here, so there is no useful
 * moment earlier to do it and nothing to mint against later.
 *
 * Errors are shown per field and recomputed on blur and on submit, never on every keystroke —
 * telling someone their email is malformed while they are three characters into typing it is
 * the thing that makes a form feel hostile.
 */
export default function CustomerForm({ data, onAttempt }) {
  const { t, i18n } = useTranslation()

  const snapshot = data.jsond_doc
  const collect = snapshot.collect || {}

  const [values, setValues] = useState({ name: '', phone: '', email: '', vatnum: '' })
  const [errors, setErrors] = useState({})
  const [touched, setTouched] = useState({})
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState(null)

  const set = (field) => (event) => {
    setValues({ ...values, [field]: event.target.value })
    // Clear this field's error as soon as it is touched again: leaving it up while somebody
    // is fixing it reads as the fix not working.
    if (errors[field]) setErrors({ ...errors, [field]: undefined })
  }

  const blur = (field) => () => {
    setTouched({ ...touched, [field]: true })
    const found = validateCustomer(values, collect)
    setErrors({ ...errors, [field]: found[field] })
  }

  const submit = async (event) => {
    event.preventDefault()

    const found = validateCustomer(values, collect)
    if (Object.keys(found).some((k) => found[k])) {
      setErrors(found)
      setTouched({ name: true, phone: true, email: true, vatnum: true })
      return
    }

    setBusy(true)
    setFailure(null)

    try {
      // language: the gateway's card form speaks the page's (PaymentRequest::attemptSnapshot)
      const res = await axios.post(apiBase() + '/paymentreq/checkout/' + data.hash_id, { ...values, language: i18n.language })
      onAttempt(res.data)
    } catch (e) {
      // The server's own reasons, each with its own sentence. A link that closed or expired
      // while this form was open is not the payer's mistake and must not read as one.
      const status = e.response && e.response.status
      const reason = e.response && e.response.data && e.response.data.error

      if (status === 409) {
        setFailure(reason === 'expired' ? 'LinkExpired' : reason === 'paid' ? 'AlreadyPaid' : 'LinkClosed')
      } else if (status === 429) {
        setFailure('TooManyAttempts')
      } else if (status === 422) {
        // The server found something this form's looser rules let through.
        setFailure(null)
        setErrors({ [reason || 'email']: 'InvalidEmail' })
      } else {
        setFailure('PaymentFailedRetry')
      }

      setBusy(false)
    }
  }

  const field = (name, extra = {}) => (
    <TextField
      required={extra.required !== false}
      fullWidth
      id={name}
      name={name}
      label={t(extra.label || name.charAt(0).toUpperCase() + name.slice(1))}
      value={values[name]}
      onChange={set(name)}
      onBlur={blur(name)}
      error={!!(touched[name] && errors[name])}
      helperText={touched[name] && errors[name] ? t(errors[name]) : ' '}
      disabled={busy}
    />
  )

  return (
    <form onSubmit={submit} noValidate>
      <Typography variant="h6" gutterBottom>{t('YourDetails')}</Typography>

      <Grid container spacing={2}>
        <Grid item xs={12}>{field('name', { label: 'Name' })}</Grid>
        <Grid item xs={12} sm={6}>{field('email', { label: 'Email' })}</Grid>
        {/* Optional: the server normalises an unusable number to '' rather than refusing it. */}
        <Grid item xs={12} sm={6}>{field('phone', { label: 'Phone', required: false })}</Grid>
        {collect.vatnum ? <Grid item xs={12} sm={6}>{field('vatnum', { label: 'VatNum' })}</Grid> : null}
      </Grid>

      {failure ? (
        <Typography variant="body1" color="error" style={{ marginTop: 16 }}>
          {t(failure)}
        </Typography>
      ) : null}

      <Button
        type="submit"
        variant="contained"
        color="primary"
        size="large"
        fullWidth
        disabled={busy}
        style={{ marginTop: 24 }}
      >
        {busy ? <CircularProgress size={24} /> : t('Continue')}
      </Button>
    </form>
  )
}
